import { ReactElement, useEffect, useState } from 'react'

import { useEventEmitter } from '@byteclaw/use-event-emitter'

import { useHandler, useOnDialogBlur, useUniqId } from '@surglogs-app/hooks'

import { $$ANSWER_EVENT, $$PROMPT_EVENT } from './constants'

export interface PromptFunctionProps<TAnswer> {
  answer(value: TAnswer): void
}

export interface PromptFunction<TAnswer> {
  (
    prompt: (props: PromptFunctionProps<TAnswer>) => ReactElement | null,
  ): Promise<TAnswer>
}

export type UsePromptOpts = Partial<{
  autoClose: boolean
  disableCloseOnBlur: boolean
}>

export function usePrompt<TAnswer>({
  autoClose = true,
  disableCloseOnBlur = false,
}: UsePromptOpts = {}) {
  const id = useUniqId()
  const eventEmitter = useEventEmitter()
  const [prompting, setPrompting] = useState(false)

  useEffect(
    () => () => {
      if (autoClose === true) {
        eventEmitter.emit($$ANSWER_EVENT, id, null)
      }
    },
    [autoClose, eventEmitter, id],
  )

  const answer = useHandler((value) => {
    eventEmitter.emit($$ANSWER_EVENT, id, value)
  })

  const { register, cleanup } = useOnDialogBlur({
    id,
    callback: () => (disableCloseOnBlur ? undefined : answer(null)),
    autoRegister: false,
  })

  const prompt: PromptFunction<TAnswer> = useHandler(
    (renderer) =>
      new Promise((resolve, reject) => {
        // subscribe to answer
        // eslint-disable-next-line @typescript-eslint/ban-types
        const handler: { unsubscribe: Function | null } = {
          unsubscribe: null,
        }

        register()

        try {
          handler.unsubscribe = eventEmitter.on(
            $$ANSWER_EVENT,
            (promptId: string, value: any) => {
              if (id === promptId) {
                setPrompting(false)

                if (handler.unsubscribe) {
                  handler.unsubscribe()
                  handler.unsubscribe = null
                }

                cleanup()
                resolve(value)
              }
            },
          )

          setPrompting(true)
          const comp = renderer({ answer })
          eventEmitter.emit($$PROMPT_EVENT, id, comp)
        } catch (e) {
          if (handler.unsubscribe) {
            handler.unsubscribe()
            handler.unsubscribe = null
          }

          setPrompting(false)
          cleanup()
          reject(e)
        }
      }),
  )

  return [prompt, prompting, id] as const
}
